import { useEffect, useMemo, useState } from 'react'
import { useBooking } from '../store/bookingStore'
import { isPastSelection } from './format'

// Params the external booking bar (the marketing-site WordPress plugin "gsm-booking-bar") appends
// when it hands a visitor straight to /rate. They mirror exactly what the in-app home widget writes
// to the store, so the funnel opens on the rate step already primed:
//   tid  = transport id (1 = Auto, 2 = Moto)     from = "YYYY-MM-DD" (check-in day)
//   time = "HH:MM" | "HH:MM:SS" (arrival hour)   to   = "YYYY-MM-DD" (check-out day; defaults to `from`)
// Legacy aliases `transport` and `hour` are accepted too.
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/
const TIME_RE = /^\d{2}:\d{2}(:\d{2})?$/
const PARAM_KEYS = ['tid', 'transport', 'from', 'time', 'hour', 'to']

interface Prefill {
  transportId: number
  from: string
  time: string // normalised to HH:MM:SS
  to: string
}

function parseBookingQuery(): Prefill | null {
  if (typeof window === 'undefined') return null
  const p = new URLSearchParams(window.location.search)
  const tidRaw = p.get('tid') ?? p.get('transport')
  const from = p.get('from') ?? ''
  const timeRaw = p.get('time') ?? p.get('hour') ?? ''
  const to = p.get('to') ?? from
  const transportId = Number(tidRaw)
  if (!tidRaw || !Number.isInteger(transportId) || transportId <= 0) return null
  if (!DAY_RE.test(from) || !DAY_RE.test(to)) return null
  // An old bookmark or a cached booking-bar link carries last week's dates; applying them would
  // prime the funnel with a selection the backend rejects. Ignore the prefill and let the visitor
  // pick dates instead.
  if (!TIME_RE.test(timeRaw)) return null
  const time = timeRaw.length === 5 ? `${timeRaw}:00` : timeRaw
  if (isPastSelection(from, time)) return null
  return { transportId, from, time, to }
}

/**
 * One-shot hydration of the booking funnel from URL params, for external entry points
 * (the website booking bar links straight to /it/rate?tid=..&from=..&time=..&to=..).
 * Returns `ready`, which is false only for the single render between mount and applying a valid
 * prefill — so the caller can hold its "need dates first" redirect until the store is populated.
 * With no (or invalid) params it returns true immediately and does nothing.
 */
export function usePrefillBookingFromQuery(): boolean {
  const setTransport = useBooking((s) => s.setTransport)
  const setDates = useBooking((s) => s.setDates)
  const prefill = useMemo(parseBookingQuery, [])
  const [ready, setReady] = useState(!prefill)

  useEffect(() => {
    if (!prefill) return
    setTransport(prefill.transportId)
    setDates(prefill.from, prefill.time, prefill.to)
    // strip the handoff params so a refresh / back-navigation doesn't re-apply a stale selection
    const url = new URL(window.location.href)
    for (const k of PARAM_KEYS) url.searchParams.delete(k)
    window.history.replaceState({}, '', url.pathname + url.search + url.hash)
    setReady(true)
  }, [prefill, setTransport, setDates])

  return ready
}
